interface Props {
  totalLeads: number;
  activeSubscriptions: number;
  emailsSent: number;
  cancelPending: number;
  noPhoneCount: number;
  noSetupCount: number;
  onFilterNoPhone?: () => void;
  onFilterNoSetup?: () => void;
}

interface Metric {
  label: string;
  value: string | number;
  icon: string;
  color: string;
  bg: string;
  sub?: string;
}

export default function MetricsCards({
  totalLeads,
  activeSubscriptions,
  emailsSent,
  cancelPending,
  noPhoneCount,
  noSetupCount,
  onFilterNoPhone,
  onFilterNoSetup,
}: Props) {
  const metrics: Metric[] = [
    {
      label: "Leads registrados",
      value: totalLeads,
      icon: "ri-user-add-line",
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
    {
      label: "Suscripciones activas",
      value: activeSubscriptions,
      icon: "ri-vip-crown-line",
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      label: "Emails de bienvenida",
      value: emailsSent,
      icon: "ri-mail-check-line",
      color: "text-sky-600",
      bg: "bg-sky-50",
      sub: totalLeads > 0 ? `${Math.round((emailsSent / totalLeads) * 100)}% tasa de envío` : "0% tasa de envío",
    },
    {
      label: "Cancelaciones pendientes",
      value: cancelPending,
      icon: "ri-close-circle-line",
      color: "text-rose-600",
      bg: "bg-rose-50",
    },
  ];

  const hasAlerts = noPhoneCount > 0 || noSetupCount > 0;

  return (
    <div className="space-y-4">
      {/* Alert cards */}
      {hasAlerts && (
        <div className="grid grid-cols-2 gap-4">
          {/* Sin número asignado */}
          <button
            onClick={onFilterNoPhone}
            className={`text-left rounded-2xl p-5 border-2 transition-all cursor-pointer group ${
              noPhoneCount > 0
                ? "bg-amber-50 border-amber-300 hover:bg-amber-100"
                : "bg-stone-50 border-stone-100 opacity-60 cursor-default"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${noPhoneCount > 0 ? "bg-amber-200" : "bg-stone-200"}`}>
                  <i className={`ri-phone-off-line text-lg ${noPhoneCount > 0 ? "text-amber-700" : "text-stone-500"}`} />
                </div>
                <div>
                  <p className={`text-3xl font-bold ${noPhoneCount > 0 ? "text-amber-800" : "text-stone-600"}`}>
                    {noPhoneCount}
                  </p>
                  <p className={`text-xs font-semibold mt-0.5 ${noPhoneCount > 0 ? "text-amber-700" : "text-stone-500"}`}>
                    Sin número Auria asignado
                  </p>
                </div>
              </div>
              {noPhoneCount > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-200 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-600 animate-pulse" />
                  <span className="text-amber-800 text-xs font-bold">Requiere acción</span>
                </div>
              )}
            </div>
            {noPhoneCount > 0 && (
              <p className="text-xs text-amber-600 mt-3 flex items-center gap-1.5 group-hover:underline">
                <i className="ri-filter-3-line" />
                Haz clic para filtrar estos clientes
              </p>
            )}
            {noPhoneCount === 0 && (
              <p className="text-xs text-stone-400 mt-3 flex items-center gap-1.5">
                <i className="ri-check-line" />
                Todos los clientes tienen número asignado
              </p>
            )}
          </button>

          {/* Sin setup completado */}
          <button
            onClick={onFilterNoSetup}
            className={`text-left rounded-2xl p-5 border-2 transition-all cursor-pointer group ${
              noSetupCount > 0
                ? "bg-orange-50 border-orange-300 hover:bg-orange-100"
                : "bg-stone-50 border-stone-100 opacity-60 cursor-default"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${noSetupCount > 0 ? "bg-orange-200" : "bg-stone-200"}`}>
                  <i className={`ri-settings-3-line text-lg ${noSetupCount > 0 ? "text-orange-700" : "text-stone-500"}`} />
                </div>
                <div>
                  <p className={`text-3xl font-bold ${noSetupCount > 0 ? "text-orange-800" : "text-stone-600"}`}>
                    {noSetupCount}
                  </p>
                  <p className={`text-xs font-semibold mt-0.5 ${noSetupCount > 0 ? "text-orange-700" : "text-stone-500"}`}>
                    Sin setup completado
                  </p>
                </div>
              </div>
              {noSetupCount > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-200 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-600 animate-pulse" />
                  <span className="text-orange-800 text-xs font-bold">Pendiente</span>
                </div>
              )}
            </div>
            {noSetupCount > 0 && (
              <p className="text-xs text-orange-600 mt-3 flex items-center gap-1.5 group-hover:underline">
                <i className="ri-filter-3-line" />
                Haz clic para filtrar estos clientes
              </p>
            )}
            {noSetupCount === 0 && (
              <p className="text-xs text-stone-400 mt-3 flex items-center gap-1.5">
                <i className="ri-check-line" />
                Todos los clientes completaron el setup
              </p>
            )}
          </button>
        </div>
      )}

      {/* Regular metrics */}
      <div className="grid grid-cols-4 gap-4">
        {metrics.map((m) => (
          <div key={m.label} className="bg-white border border-stone-100 rounded-2xl p-5">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${m.bg}`}>
                <i className={`${m.icon} ${m.color} text-lg`} />
              </div>
            </div>
            <p className="text-3xl font-bold text-stone-900 mb-1">{m.value}</p>
            <p className="text-xs text-stone-500 font-medium">{m.label}</p>
            {m.sub && <p className="text-xs text-stone-400 mt-0.5">{m.sub}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
